import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

// Loads Cloudflare's Turnstile script once, no matter how many times/places
// this component gets mounted (register form, login form, etc. all share it).
let scriptLoadingPromise = null;
function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    if (scriptLoadingPromise) return scriptLoadingPromise;
    scriptLoadingPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('No se pudo cargar Cloudflare Turnstile'));
        document.head.appendChild(script);
    });
    return scriptLoadingPromise;
}

/**
 * Cloudflare Turnstile widget (anti-bot). Renders itself once mounted and
 * reports the verification token via `onVerify`. Tokens are single-use —
 * after any submit attempt (success or failure) call `ref.current.reset()`
 * to get a fresh one before the user can submit again.
 *
 * Requires REACT_APP_TURNSTILE_SITE_KEY to be set; if it isn't, the widget
 * quietly doesn't render (matches the backend, which skips verification when
 * its own TURNSTILE_SECRET_KEY isn't configured — e.g. local dev).
 */
const Turnstile = forwardRef(function Turnstile({ onVerify, onExpire, onError }, ref) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
        if (!siteKey) {
            console.warn('REACT_APP_TURNSTILE_SITE_KEY no esta configurada — el widget de Turnstile no se muestra.');
            return;
        }

        loadTurnstileScript()
            .then(() => {
                if (cancelled || !containerRef.current || !window.turnstile) return;
                widgetIdRef.current = window.turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    theme: 'dark',
                    callback: (token) => onVerify?.(token),
                    'expired-callback': () => onExpire?.(),
                    'error-callback': () => onError?.(),
                });
            })
            .catch((err) => {
                console.error(err);
                onError?.();
            });

        return () => {
            cancelled = true;
            if (widgetIdRef.current != null && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(ref, () => ({
        reset: () => {
            if (widgetIdRef.current != null && window.turnstile) {
                window.turnstile.reset(widgetIdRef.current);
            }
        },
    }));

    return <div ref={containerRef} className="flex justify-center" />;
});

export default Turnstile;
